import { useState, useEffect } from 'react';
import { getSettings, saveSettings } from '@/utils/storage';

type Theme = 'light' | 'dark' | 'system';

export function useTheme() {
  const [theme, setTheme] = useState<Theme>('system');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Initial load from storage
    const loadTheme = async () => {
        try {
            const settings = await getSettings();
            if (settings && settings.theme) {
                 setTheme(settings.theme);
            }
        } finally {
            setIsLoaded(true);
        }
    };
    loadTheme();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');

    const applyTheme = (t: Theme) => {
        if (t === 'system') {
            const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            root.classList.add(systemDark ? 'dark' : 'light');
        } else {
            root.classList.add(t);
        }
    };

    applyTheme(theme);

    // Save to storage
    const save = async () => {
        if (!isLoaded) return; // Skip save if not loaded yet

        try {
            const settings = await getSettings();
            // Only save if different to prevent loops, and ensure we have settings loaded
            if (settings && settings.theme !== theme) {
                await saveSettings({ ...settings, theme });
            }
        } catch (e) {
            console.warn('Failed to persist theme settings:', e);
        }
    };
    save();

  }, [theme, isLoaded]);

  // Listen for storage changes to sync across tabs/windows
  useEffect(() => {
    const handleStorageChange = (changes: { [key: string]: chrome.storage.StorageChange }, areaName: string) => {
      if (areaName === 'local' && changes.settings) {
        const newSettings = changes.settings.newValue as any;
        if (newSettings && newSettings.theme && newSettings.theme !== theme) {
          setTheme(newSettings.theme);
        }
      }
    };

    if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.onChanged) {
      chrome.storage.onChanged.addListener(handleStorageChange);
      return () => chrome.storage.onChanged.removeListener(handleStorageChange);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => {
        if (prev === 'light') return 'dark';
        if (prev === 'dark') return 'system';
        return 'light';
    });
  };

  const setThemeValue = (newTheme: Theme) => {
      setTheme(newTheme);
  }

  return {
    theme,
    toggleTheme,
    setTheme: setThemeValue,
    isDark: document.documentElement.classList.contains('dark')
  };
}
